import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, ShoppingCart, ChevronDown, ChevronUp, Star, ShieldCheck, Download, CreditCard, CheckCircle2, Clock, Calendar, HeartPulse } from 'lucide-react';
import { KITS } from '../data/kits';
import { UPSELLS } from '../data/upsells';
interface ProductCollectionProps {
  selectedKitIds: string[];
  selectedUpsellIds: string[];
  onToggleKit: (id: string) => void;
  onToggleUpsell: (id: string) => void;
}
const BASE_CHECKLIST = ['Más de 100 recetas', 'Lista de compras', 'Formato PDF', 'Organización semanal', 'Acceso inmediato'];
// Bonos que hoy se llevan GRATIS con la promo (precio original tachado).
const BONUS_PRICES: Record<string, number> = {
  'Más de 100 recetas': 9990,
  'Lista de compras': 4990,
  'Organización semanal': 4990
};
// Recetas específicas por kit (se muestran arriba de los ítems generales)
const KIT_RECIPES: Record<string, string[]> = {
  completa: ['40 recetas para desayunos y meriendas', '40 recetas para almuerzos y cenas', '50 recetas para diabéticos'],
  fitness: ['40 recetas para desayunos y meriendas proteicos', '40 recetas para almuerzos y cenas proteicos', '50 recetas proteicas con proteína en polvo (whey protein)'],
  cardio: ['40 recetas descenso de peso', '40 recetas colesterol/triglicéridos', '50 recetas para hipertensos'],
  alergias: ['40 recetas de almuerzos y cenas para alergias', '40 recetas de desayunos y meriendas para alergias', '40 recetas saludables para alergias'],
  lactosa: ['30 recetas para almuerzos y cenas sin lactosa', '30 recetas de desayunos y meriendas sin lactosa', '30 recetas saludables sin lactosa'],
  colon: ['40 recetas para almuerzos y cenas para colon irritable', '40 recetas de desayunos y meriendas para colon irritable', '40 recetas saludables para colon irritable'],
  antiinflamatorio: ['40 recetas para almuerzos y cenas antiinflamatorias', '40 recetas de desayunos y meriendas antiinflamatorias', '40 recetas saludables antiinflamatorias'],
  hipotiroidismo: ['40 recetas para almuerzos y cenas para hipotiroidismo', '40 recetas de desayunos y meriendas para hipotiroidismo', '40 recetas saludables para hipotiroidismo'],
  menopausia: ['40 recetas para almuerzos y cenas para la post menopausia', '40 recetas de desayunos y meriendas para la post menopausia', '40 recetas saludables para equilibrio hormonal y bienestar']
};
// Descripción "ideal para" por kit
const KIT_IDEAL_FOR: Record<string, string> = {
  completa: 'Personas que quieren ordenar su alimentación desde cero, con recetas equilibradas para toda la semana y opciones pensadas para cuidar los niveles de azúcar.',
  fitness: 'Quienes entrenan y buscan potenciar sus resultados con comidas ricas en proteínas, prácticas de preparar y pensadas para acompañar cada objetivo deportivo.',
  cardio: 'Personas que quieren cuidar su corazón con un plan orientado al descenso de peso, al control del colesterol y los triglicéridos, y a una alimentación amigable para la presión arterial.',
  alergias: 'Quienes necesitan evitar los alérgenos más comunes y aún así disfrutar de recetas variadas, seguras y fáciles de preparar para toda la familia.',
  lactosa: 'Personas que buscan comer rico sin lactosa, con alternativas deliciosas para el día a día que evitan molestias y no resignan sabor.',
  colon: 'Quienes quieren cuidar su digestión con recetas suaves y bien toleradas, ideales para acompañar un estilo de vida tranquilo para el intestino.',
  antiinflamatorio: 'Personas que buscan priorizar ingredientes que ayudan a reducir la inflamación y sumar más energía y bienestar a su rutina diaria.',
  hipotiroidismo: 'Quienes desean acompañar su bienestar con recetas pensadas para una alimentación equilibrada, sabiendo qué priorizar en cada comida.',
  menopausia: 'Personas que atraviesan esta etapa y buscan recetas que acompañen el equilibrio hormonal, el bienestar general y el control del peso de forma natural.'
};
const GALLERY_IMAGES = ['https://images.unsplash.com/photo-1490645935967-10de6ba17061?auto=format&fit=crop&q=80&w=600', 'https://images.unsplash.com/photo-1512621776951-a57141f2eefd?auto=format&fit=crop&q=80&w=600', 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?auto=format&fit=crop&q=80&w=600'];
// Galería "¿Qué incluye este kit?" personalizada por kit
// [0] = imagen grande superior, [1] = inferior izquierda, [2] = inferior derecha
const KIT_GALLERY: Record<string, string[]> = {
  // Alimentación Completa — comidas equilibradas + meal prep + bowl
  completa: ['https://images.unsplash.com/photo-1498837167922-ddd27525d352?auto=format&fit=crop&q=80&w=900', 'https://images.unsplash.com/photo-1547592166-23ac45744acd?auto=format&fit=crop&q=80&w=600', 'https://images.unsplash.com/photo-1512621776951-a57141f2eefd?auto=format&fit=crop&q=80&w=600'],
  // Fitness & Proteínas — pollo grillado, pancakes proteicos, carne magra
  fitness: ['https://images.unsplash.com/photo-1532550907401-a500c9a57435?auto=format&fit=crop&q=80&w=900', 'https://images.unsplash.com/photo-1567620905732-2d1ec7ab7445?auto=format&fit=crop&q=80&w=600', 'https://images.unsplash.com/photo-1600891964092-4316c288032e?auto=format&fit=crop&q=80&w=600'],
  // Salud Cardiovascular — salmón, vegetales, ensalada saludable
  cardio: ['https://images.unsplash.com/photo-1467003909585-2f8a72700288?auto=format&fit=crop&q=80&w=900', 'https://images.unsplash.com/photo-1540420773420-3366772f4999?auto=format&fit=crop&q=80&w=600', 'https://images.unsplash.com/photo-1540189549336-e6e99c3679fe?auto=format&fit=crop&q=80&w=600'],
  // Alergias Alimentarias — yogur y granola, frutas, ensalada segura
  alergias: ['https://images.unsplash.com/photo-1488477181946-6428a0291777?auto=format&fit=crop&q=80&w=900', 'https://images.unsplash.com/photo-1482049016688-2d3e1b311543?auto=format&fit=crop&q=80&w=600', 'https://images.unsplash.com/photo-1511690656952-34342bb7c2f2?auto=format&fit=crop&q=80&w=600'],
  // Sin Lactosa — bowl vegetal, palta, tostada de palta
  lactosa: ['https://images.unsplash.com/photo-1546069901-ba9599a7e63c?auto=format&fit=crop&q=80&w=900', 'https://images.unsplash.com/photo-1494390248081-4e521a5940db?auto=format&fit=crop&q=80&w=600', 'https://images.unsplash.com/photo-1504754524776-8f4f37790ca0?auto=format&fit=crop&q=80&w=600'],
  // Colon Irritable — recetas suaves: tostada francesa, pancakes, desayuno liviano
  colon: ['https://images.unsplash.com/photo-1484723091739-30a097e8f929?auto=format&fit=crop&q=80&w=900', 'https://images.unsplash.com/photo-1490818387583-1baba5e638af?auto=format&fit=crop&q=80&w=600', 'https://images.unsplash.com/photo-1455619452474-d2be8b1e70cd?auto=format&fit=crop&q=80&w=600'],
  // Antiinflamatorio — smoothie, vegetales coloridos, plato saludable
  antiinflamatorio: ['https://images.unsplash.com/photo-1502741224143-90386d7f8c82?auto=format&fit=crop&q=80&w=900', 'https://images.unsplash.com/photo-1466637574441-749b8f19452f?auto=format&fit=crop&q=80&w=600', 'https://images.unsplash.com/photo-1490645935967-10de6ba17061?auto=format&fit=crop&q=80&w=600'],
  // Hipotiroidismo — comida completa, pescado con vegetales, sopa nutritiva
  hipotiroidismo: ['https://images.unsplash.com/photo-1504674900247-0877df9cc836?auto=format&fit=crop&q=80&w=900', 'https://images.unsplash.com/photo-1432139555190-58524dae6a55?auto=format&fit=crop&q=80&w=600', 'https://images.unsplash.com/photo-1476718406336-bb5a9690ee2a?auto=format&fit=crop&q=80&w=600'],
  // Post Menopausia — desayuno con frutas, avena, bowl con semillas
  menopausia: ['https://images.unsplash.com/photo-1505253716362-afaea1d3d1af?auto=format&fit=crop&q=80&w=900', 'https://images.unsplash.com/photo-1517673400267-0251440c45dc?auto=format&fit=crop&q=80&w=600', 'https://images.unsplash.com/photo-1494597564530-871f2b93ac55?auto=format&fit=crop&q=80&w=600']
};
// Imagen de "Páginas de recetas" en la vista previa, por kit
const KIT_RECIPE_PREVIEW: Record<string, string> = {
  completa: 'https://cdn.magicpatterns.com/uploads/5vnsQzJ6MZ666ctbvgi2jW/ChatGPT_Image_29_jun_2026,_01_01_45_p.m..png',
  colon: 'https://cdn.magicpatterns.com/uploads/fhQtsUViRJLBqRMcdxrQzb/kit_colon_irritable.png',
  lactosa: 'https://cdn.magicpatterns.com/uploads/jLLNqPgGE9TonTYX6NuAxZ/Kit_sin_lactosa.png',
  alergias: 'https://cdn.magicpatterns.com/uploads/37UUG9RBrVhSewhgJvwv5Q/Kit_alergias.png',
  fitness: 'https://cdn.magicpatterns.com/uploads/1iyb6dgsHK8gNL7tRFpoTo/Kit_fitness.png',
  cardio: 'https://cdn.magicpatterns.com/uploads/6DcUPiDKxMAzY7JooJW1U2/Receta_salud_cardiovascular.png',
  antiinflamatorio: 'https://cdn.magicpatterns.com/uploads/mNpQVXbEjS9noi9vHcaAE7/Kit_antiinflamatorio.png',
  hipotiroidismo: 'https://cdn.magicpatterns.com/uploads/gAA2BikVywTKeNKYD4FMua/Kit_hipotiroidismo.png',
  menopausia: 'https://cdn.magicpatterns.com/uploads/5qFmMuE4AdetQgPS1EF38U/Kit_post_menopausia.png'
};
export function ProductCollection({
  selectedKitIds,
  selectedUpsellIds,
  onToggleKit,
  onToggleUpsell
}: ProductCollectionProps) {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const expandedRef = useRef<HTMLDivElement | null>(null);
  const allProducts = [...KITS.map(k => ({
    ...k,
    type: 'kit' as const
  })), ...UPSELLS.map(u => ({
    ...u,
    type: 'upsell' as const
  }))];
  useEffect(() => {
    if (expandedId && expandedRef.current) {
      setTimeout(() => {
        expandedRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }, 100);
    }
  }, [expandedId]);
  return <section id="kits" className="py-24 bg-cream scroll-mt-20" data-id="element-699">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-700">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-701">
          <h2 className="text-3xl md:text-4xl font-bold text-ink mb-6 tracking-tight" data-id="element-702">
            Explorá todos los kits
          </h2>
          <p className="text-lg text-ink-light font-light" data-id="element-703">
            Conocé nuestra colección completa y elegí el que mejor se adapta a
            tus objetivos.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-704">
          {allProducts.map((product, index) => {
          const isSelected = product.type === 'kit' ? selectedKitIds.includes(product.id) : selectedUpsellIds.includes(product.id);
          const isExpanded = expandedId === product.id;
          const handleToggle = (e: React.MouseEvent) => {
            e.stopPropagation();
            if (product.type === 'kit') onToggleKit(product.id);else onToggleUpsell(product.id);
          };
          const toggleExpand = () => {
            setExpandedId(isExpanded ? null : product.id);
          };
          return <motion.div key={product.id} ref={isExpanded ? expandedRef : null} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.05,
            duration: 0.5
          }} className={`flex flex-col bg-white rounded-3xl overflow-hidden transition-all duration-300 border border-ink/5 shadow-premium ${isExpanded ? 'ring-2 ring-combo-green md:col-span-2 lg:col-span-3' : 'hover:shadow-premium-hover hover:-translate-y-1'}`} data-id="element-705">
                <div className={`flex flex-col ${isExpanded ? 'lg:flex-row' : ''}`} data-id="element-706">
                  {/* Header / Collapsed View */}
                  <div className={`flex flex-col ${isExpanded ? 'lg:w-1/3 border-b lg:border-b-0 lg:border-r border-ink/5' : 'flex-grow'}`} data-id="element-707">
                    <div className="relative aspect-[4/3] bg-cream-dark p-6 flex items-center justify-center overflow-hidden cursor-pointer" onClick={toggleExpand} data-id="element-708">
                      <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/5" data-id="element-709"></div>
                      {product.image ? <img src={product.image} alt={product.name} className="relative z-10 w-full h-full object-contain drop-shadow-xl" data-id="element-710" /> : <div className="relative z-10 w-32 h-40 bg-white rounded-lg shadow-md flex items-center justify-center text-ink-light" data-id="element-711">
                          Mockup
                        </div>}
                      {product.badge && <div className="absolute top-4 left-4 z-20 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-[10px] font-bold tracking-wider text-ink uppercase shadow-sm" data-id="element-712">
                          {product.badge}
                        </div>}
                    </div>

                    <div className="p-6 flex flex-col flex-grow" data-id="element-713">
                      <div className="mb-4 flex-grow cursor-pointer" onClick={toggleExpand} data-id="element-714">
                        <h3 className="text-xl font-bold text-ink mb-2 leading-tight flex items-center gap-2" data-id="element-715">
                          {product.name}
                        </h3>
                        <p className="text-sm text-ink-light line-clamp-2" data-id="element-716">
                          {product.description}
                        </p>
                      </div>

                      <div className="flex items-center gap-1 mb-4" data-id="element-717">
                        {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 text-gold fill-gold" data-id="element-718" />)}
                        <span className="text-xs font-bold text-ink ml-1" data-id="element-719">
                          5.0
                        </span>
                      </div>

                      <div className="flex flex-wrap gap-2 mb-6" data-id="element-720">
                        <span className="text-[10px] font-bold bg-combo-green/10 text-combo-green px-2 py-1 rounded-sm uppercase tracking-wider" data-id="element-721">
                          Más vendido
                        </span>
                        <span className="text-[10px] font-bold bg-ink/5 text-ink px-2 py-1 rounded-sm uppercase tracking-wider" data-id="element-722">
                          Acceso inmediato
                        </span>
                        <span className="text-[10px] font-bold bg-ink/5 text-ink px-2 py-1 rounded-sm uppercase tracking-wider" data-id="element-723">
                          Formato PDF
                        </span>
                      </div>

                      {!isExpanded && <button onClick={toggleExpand} className="w-full py-3.5 rounded-full font-bold text-sm transition-all flex items-center justify-center gap-2 bg-combo-green text-white shadow-glow hover:bg-combo-green/90" data-id="element-724">
                          Ver contenido <ChevronDown className="w-4 h-4" data-id="element-725" />
                        </button>}
                    </div>
                  </div>

                  {/* Expanded Content */}
                  <AnimatePresence data-id="element-726">
                    {isExpanded && <motion.div initial={{
                  opacity: 0,
                  height: 0
                }} animate={{
                  opacity: 1,
                  height: 'auto'
                }} exit={{
                  opacity: 0,
                  height: 0
                }} transition={{
                  duration: 0.3
                }} className="flex-1 flex flex-col bg-white" data-id="element-727">
                        <div className="p-6 lg:p-10 space-y-12" data-id="element-728">
                          {/* Gallery & Checklist */}
                          <div className="grid md:grid-cols-2 gap-10" data-id="element-729">
                            <div data-id="element-730">
                              <h4 className="text-2xl font-bold text-ink mb-6" data-id="element-731">
                                ¿Qué incluye este kit?
                              </h4>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-732">
                                {[...(KIT_RECIPES[product.id] ?? []), ...BASE_CHECKLIST].map((item, i) => {
                            const bonusPrice = BONUS_PRICES[item];
                            return <div key={i} className="flex items-center gap-2 text-sm text-ink-light" data-id="element-733">
                                      <CheckCircle2 className="w-4 h-4 text-combo-green flex-shrink-0" data-id="element-734" />
                                      <span className="flex flex-wrap items-center gap-1.5" data-id="element-735">
                                        <span data-id="element-736">{item}</span>
                                        {bonusPrice && <>
                                            <span className="text-ink-light/70 line-through tabular-nums" data-id="element-737">
                                              $
                                              {bonusPrice.toLocaleString('es-AR')}
                                            </span>
                                            <span className="text-[10px] font-bold bg-combo-green/10 text-combo-green px-2 py-0.5 rounded-full uppercase tracking-wider" data-id="element-738">
                                              Hoy gratis
                                            </span>
                                          </>}
                                      </span>
                                    </div>;
                          })}
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4" data-id="element-739">
                              <img src={(KIT_GALLERY[product.id] ?? GALLERY_IMAGES)[0]} alt="Comidas saludables y equilibradas" className="w-full h-32 object-cover rounded-2xl col-span-2" data-id="element-740" />
                              <img src={(KIT_GALLERY[product.id] ?? GALLERY_IMAGES)[1]} alt="Viandas y preparación de comidas" className="w-full h-24 object-cover rounded-2xl" data-id="element-741" />
                              <img src={(KIT_GALLERY[product.id] ?? GALLERY_IMAGES)[2]} alt="Bowl saludable balanceado" className="w-full h-24 object-cover rounded-2xl" data-id="element-742" />
                            </div>
                          </div>

                          {/* Preview Section */}
                          <div className="pt-8 border-t border-ink/5" data-id="element-743">
                            <h4 className="text-2xl font-bold text-ink mb-6" data-id="element-744">
                              Vista previa
                            </h4>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-745">
                              {/* Páginas de recetas — imagen solo para el Kit Alimentación Completa */}
                              <div className="flex flex-col gap-2" data-id="element-746">
                                <div className="aspect-[4/3] bg-cream rounded-xl border border-ink/10 overflow-hidden flex items-center justify-center p-4" data-id="element-747">
                                  {KIT_RECIPE_PREVIEW[product.id] ? <img src={KIT_RECIPE_PREVIEW[product.id]} alt="Páginas de recetas" className="w-full h-full object-cover rounded-lg" data-id="element-748" /> : <span className="text-xs font-bold text-ink-light text-center" data-id="element-749">
                                      Páginas de recetas
                                    </span>}
                                </div>
                                <span className="text-xs font-bold text-ink-light text-center" data-id="element-750">
                                  Páginas de recetas
                                </span>
                              </div>
                              {/* Lista de compras — misma imagen para todos los kits */}
                              <div className="flex flex-col gap-2" data-id="element-751">
                                <div className="aspect-[4/3] bg-cream rounded-xl border border-ink/10 overflow-hidden" data-id="element-752">
                                  <img src="https://cdn.magicpatterns.com/uploads/wzBJgZbBNjLdFvmogxU92k/ChatGPT_Image_29_jun_2026,_01_28_53_p.m..png" alt="Lista de compras" className="w-full h-full object-cover" data-id="element-753" />
                                </div>
                                <span className="text-xs font-bold text-ink-light text-center" data-id="element-754">
                                  Lista de compras
                                </span>
                              </div>
                              {/* Planificador semanal — misma imagen para todos los kits */}
                              <div className="flex flex-col gap-2" data-id="element-755">
                                <div className="aspect-[4/3] bg-cream rounded-xl border border-ink/10 overflow-hidden" data-id="element-756">
                                  <img src="https://cdn.magicpatterns.com/uploads/oTz34HERSCbnKkM6zUPNvx/ChatGPT_Image_29_jun_2026,_01_37_42_p.m..png" alt="Planificador semanal" className="w-full h-full object-cover" data-id="element-757" />
                                </div>
                                <span className="text-xs font-bold text-ink-light text-center" data-id="element-758">
                                  Planificador semanal
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* Benefits */}
                          <div className="pt-8 border-t border-ink/5" data-id="element-759">
                            <h4 className="text-2xl font-bold text-ink mb-6" data-id="element-760">
                              ¿Por qué elegir este kit?
                            </h4>
                            <div className="grid sm:grid-cols-3 gap-6" data-id="element-761">
                              <div className="bg-cream p-5 rounded-2xl" data-id="element-762">
                                <Clock className="w-6 h-6 text-combo-green mb-3" data-id="element-763" />
                                <h5 className="font-bold text-ink mb-1" data-id="element-764">
                                  Ahorrá tiempo
                                </h5>
                                <p className="text-xs text-ink-light" data-id="element-765">
                                  Recetas rápidas y prácticas para tu día a día.
                                </p>
                              </div>
                              <div className="bg-cream p-5 rounded-2xl" data-id="element-766">
                                <Calendar className="w-6 h-6 text-combo-green mb-3" data-id="element-767" />
                                <h5 className="font-bold text-ink mb-1" data-id="element-768">
                                  Planificación
                                </h5>
                                <p className="text-xs text-ink-light" data-id="element-769">
                                  Menú semanal organizado paso a paso.
                                </p>
                              </div>
                              <div className="bg-cream p-5 rounded-2xl" data-id="element-770">
                                <HeartPulse className="w-6 h-6 text-combo-green mb-3" data-id="element-771" />
                                <h5 className="font-bold text-ink mb-1" data-id="element-772">
                                  Bienestar
                                </h5>
                                <p className="text-xs text-ink-light" data-id="element-773">
                                  Ingredientes seleccionados para tu objetivo.
                                </p>
                              </div>
                            </div>
                          </div>

                          {/* Ideal For */}
                          <div className="bg-cream-dark p-8 rounded-3xl" data-id="element-774">
                            <h4 className="text-xl font-bold text-ink mb-4" data-id="element-775">
                              Este kit es ideal para...
                            </h4>
                            <p className="text-ink-light leading-relaxed" data-id="element-776">
                              {KIT_IDEAL_FOR[product.id] ?? 'Personas que buscan organizar sus comidas semanales con opciones saludables, prácticas y deliciosas.'}
                            </p>
                          </div>

                          {/* Trust Badges */}
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-6 border-t border-ink/5" data-id="element-777">
                            <div className="flex flex-col items-center text-center gap-2" data-id="element-778">
                              <Star className="w-5 h-5 text-gold fill-gold" data-id="element-779" />
                              <span className="text-xs font-bold text-ink" data-id="element-780">
                                Más de 7.000 clientes
                              </span>
                            </div>
                            <div className="flex flex-col items-center text-center gap-2" data-id="element-781">
                              <ShieldCheck className="w-5 h-5 text-combo-green" data-id="element-782" />
                              <span className="text-xs font-bold text-ink" data-id="element-783">
                                Compra segura
                              </span>
                            </div>
                            <div className="flex flex-col items-center text-center gap-2" data-id="element-784">
                              <Download className="w-5 h-5 text-combo-green" data-id="element-785" />
                              <span className="text-xs font-bold text-ink" data-id="element-786">
                                Descarga inmediata
                              </span>
                            </div>
                            <div className="flex flex-col items-center text-center gap-2" data-id="element-787">
                              <CreditCard className="w-5 h-5 text-combo-green" data-id="element-788" />
                              <span className="text-xs font-bold text-ink" data-id="element-789">
                                Todos los medios de pago
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Sticky CTA Footer */}
                        <div className="sticky bottom-0 bg-white/90 backdrop-blur-md p-4 sm:p-6 border-t border-ink/5 flex flex-col sm:flex-row items-center justify-between gap-4 z-20" data-id="element-790">
                          <div className="flex items-end gap-3" data-id="element-791">
                            <div className="text-sm text-ink-light line-through mb-1" data-id="element-792">
                              ${product.oldPrice.toLocaleString('es-AR')}
                            </div>
                            <div className="text-3xl font-bold text-combo-green" data-id="element-793">
                              ${product.price.toLocaleString('es-AR')}
                            </div>
                          </div>
                          <div className="flex gap-3 w-full sm:w-auto" data-id="element-794">
                            <button onClick={toggleExpand} className="px-4 py-3.5 rounded-full font-bold text-sm bg-cream text-ink hover:bg-cream-dark transition-all flex-shrink-0" data-id="element-795">
                              <ChevronUp className="w-5 h-5" data-id="element-796" />
                            </button>
                            <button onClick={handleToggle} className={`flex-1 sm:flex-none px-8 py-3.5 rounded-full font-bold text-sm transition-all flex items-center justify-center gap-2 ${isSelected ? 'bg-ink text-white shadow-glow' : 'bg-combo-green text-white shadow-glow hover:bg-combo-green/90'}`} data-id="element-797">
                              {isSelected ? <>
                                  <Check className="w-4 h-4" data-id="element-798" /> SELECCIONADO
                                </> : <>
                                  <ShoppingCart className="w-4 h-4" data-id="element-799" /> QUIERO
                                  ESTE KIT
                                </>}
                            </button>
                          </div>
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </div>
              </motion.div>;
        })}
        </div>
      </div>
    </section>;
}